import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import axios from "axios";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLoader } from "@fortawesome/pro-regular-svg-icons";

import { useDispatch } from "react-redux";
import { getBooking } from "../../store/booking/bookingActions";

export default function FileUpload({ 
    current_booking, 
    setCurrentBooking, 
    url, 
    file_type, 
    type, 
    tag_id, 
    context, 
    onUploadSuccess,
    onUploadComplete,
    isJoint = false
}) {
    const { current_staff } = useSelector((state) => state.auth);
    const [uploading, setUploading] = useState(false);
    const dispatch = useDispatch();

    const onDrop = useCallback(async (acceptedFiles) => {
        setUploading(true);
        try {
            const uploadedFiles = await Promise.all(
                acceptedFiles.map(uploadFile)
            );
            if (onUploadSuccess) {
                onUploadSuccess(uploadedFiles);
            }
            if (onUploadComplete) {
                onUploadComplete(uploadedFiles);
            }
        } catch (error) {
            console.error('Error uploading files:', error);
        } finally {
            setUploading(false);
        }
    }, [context, current_booking, current_staff, isJoint]);

    async function uploadFile(file) {
        const form_data = new FormData();
        
        // Always use the booking's user ID for the file owner
        // current_booking.user.id should be passed from parent component
        const userId = current_booking?.user?.id;
        if (!userId) {
            throw new Error('No user ID available for file upload');
        }
        
        const bookingId = current_booking?.id || context?.booking_id;
        
        form_data.append("file", file);
        form_data.append("user_id", userId);
        form_data.append("added_by", current_staff?.id || userId);
        
        if (bookingId) {
            form_data.append("booking_id", bookingId);
        }
        
        if (file_type) {
            form_data.append("file_type", file_type);
        }
        
        if (isJoint) {
            form_data.append("is_joint", "1");
        }

        if (current_staff) {
            form_data.append("logged_user_id", current_staff.id);
            form_data.append("name", current_staff.name);
            form_data.append("surname", current_staff.surname);
            if (type) form_data.append("type", type);
            if (tag_id) form_data.append("tag_id", tag_id);
        }
        
        // For TPN context, add specific type
        if (context?.type === 'user_tpn') {
            form_data.append("context_type", 'user_tpn');
            if (context.id) {
                form_data.append("context_id", context.id);
            }
        }

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}upload_file`, form_data, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                },
                withCredentials: true
            });
            
            // For TPN context, we don't need to refresh the booking
            if (context?.type === 'user_tpn') {
                return response.data.file; // Return the uploaded file data
            }
            
            // Original booking refresh logic - only if no onUploadComplete callback
            if (!onUploadComplete) {
                if (setCurrentBooking) {
                    const bookingUrl = url || current_booking?.url || 
                        (current_booking?.edit_link?.edit_url || current_booking?.user?.edit_url);
                    
                    if (bookingUrl) {
                        const bookingResponse = await axios.get(`${process.env.REACT_APP_API_URL}booking_url/${bookingUrl}`);
                        setCurrentBooking(bookingResponse.data);
                    }
                } else if (current_booking?.id) {
                    dispatch(getBooking(current_booking.id));
                }
            }
            
            return response.data.file;
        } catch (error) {
            console.error('Error uploading file:', error);
            throw error; // Re-throw to be caught by Promise.all
        }
    }

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
    });

    return (
        <div className="bg-light_background mt-1 sm:mt-0 sm:col-span-2 w-full" {...getRootProps()}>
            <div className="w-full flex justify-center items-center px-4 py-3 border-2 border-dark_background border-dashed rounded-md">
                <div className="flex items-center space-x-3">
                    {!uploading ? (
                        <svg className="h-8 w-8 text-gray-400 flex-shrink-0" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                            <path
                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    ) : (
                        <FontAwesomeIcon icon={faLoader} className="animate-spin h-8 w-8 text-gray-400 flex-shrink-0" />
                    )}
                    <div className="text-sm text-gray-600">
                        <label htmlFor="file-upload" className="relative cursor-pointer bg-light_background rounded-md font-medium text-primary hover:text-primary_hover focus-within:outline-none">
                            {!uploading ? <span>Drop files here or click to upload</span> : <span>Files uploading...</span>}
                            <input {...getInputProps()} />
                        </label>
                        {!uploading && <span className="text-xs text-gray-500 ml-2">(up to 10MB)</span>}
                    </div>
                </div>
            </div>
        </div>
    );
}
